import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, LockKeyhole, AlertTriangle, FileKey2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const money = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not provided";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const title = (value) => {
  if (!value) {
    return "Not provided";
  }
  return String(value).replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

function FieldGrid({ title: heading, fields, testId }) {
  return (
    <section
      className="border-t border-slate-200 py-6 first:border-t-0 first:pt-0"
      data-testid={testId}
    >
      <h2 className="font-display text-xl font-black text-slate-950">{heading}</h2>
      <dl className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {fields.map(([label, value, isSensitive]) => (
          <div key={label} className="min-w-0 border-b border-slate-100 pb-3">
            <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</dt>
            <dd
              className={`mt-1 break-words text-sm font-semibold text-slate-900 ${
                isSensitive ? "font-mono text-base text-rose-700" : ""
              }`}
              data-testid={`${testId}-${label.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-")}`}
            >
              {value || "Not provided"}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function DealerApplication() {
  const { linkToken } = useParams();
  const [code, setCode] = useState("");
  const [application, setApplication] = useState(null);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState("");

  const loadApplication = useCallback(async () => {
    const { data } = await api.get(`/dealer-application/${linkToken}`);
    setApplication(data);
  }, [linkToken]);

  useEffect(() => {
    loadApplication()
      .catch(() => null)
      .finally(() => setChecking(false));
  }, [loadApplication]);

  const verify = async (event) => {
    event.preventDefault();
    if (!/^\d{8}$/.test(code)) {
      setError("Enter the 8-digit one-time code from the AutoBroker email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post(`/dealer-application/${linkToken}/verify`, { code });
      setCode("");
      await loadApplication();
    } catch {
      setError("This secure access link or code is unavailable. Contact your AutoBroker broker.");
    } finally {
      setBusy(false);
    }
  };

  const acknowledgeReceipt = async () => {
    setAcknowledging(true);
    setError("");
    try {
      const { data } = await api.post(`/dealer-application/${linkToken}/acknowledge`);
      setApplication((current) => ({
        ...current,
        receipt: { acknowledged_at: data.acknowledged_at },
      }));
    } catch {
      setError("This secure session is no longer available. Contact your AutoBroker broker.");
    } finally {
      setAcknowledging(false);
    }
  };

  if (checking) {
    return <main className="min-h-screen bg-slate-950" data-testid="dealer-view-loading" />;
  }

  if (!application) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-lg ab-fade-up">
          <div className="border border-white/15 bg-slate-900 p-6 shadow-2xl sm:p-8">
            <div className="flex items-center justify-between border-b border-white/10 pb-5">
              <span className="font-display text-2xl font-black text-white">AutoBroker</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-rose-300">
                <LockKeyhole className="h-3.5 w-3.5" /> Secure dealer view
              </span>
            </div>
            <div className="mt-8">
              <div className="flex h-12 w-12 items-center justify-center bg-rose-600 text-white">
                <FileKey2 className="h-6 w-6" />
              </div>
              <h1 className="mt-5 font-display text-3xl font-black text-white">
                Open the full application
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Use the eight-digit one-time code included in your AutoBroker dealer packet.
                This code can be used once, and the resulting view is time-limited.
              </p>
            </div>
            <form className="mt-7" onSubmit={verify} data-testid="dealer-code-form">
              <label
                className="text-xs font-bold uppercase tracking-wide text-slate-300"
                htmlFor="dealer-one-time-code"
              >
                One-time code
              </label>
              <Input
                id="dealer-one-time-code"
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={8}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                placeholder="00000000"
                className="mt-2 h-14 border-white/15 bg-slate-950 font-mono text-xl tracking-[0.25em] text-white"
                data-testid="dealer-code-input"
              />
              {error && (
                <p
                  className="mt-3 flex gap-2 text-sm leading-5 text-rose-300"
                  data-testid="dealer-code-error"
                  role="alert"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={busy}
                className="mt-6 h-12 w-full rounded-none bg-rose-600 font-bold hover:bg-rose-500"
                data-testid="dealer-code-submit"
              >
                {busy ? "Verifying secure access…" : "Open application"}
              </Button>
            </form>
            <p className="mt-6 border-t border-white/10 pt-4 text-xs leading-5 text-slate-400">
              This page contains confidential applicant data for this transaction only.
              Do not forward, download, or retain it outside your authorized process.
            </p>
          </div>
        </div>
      </main>
    );
  }

  const { applicant, financing, prequalification, vehicle, receipt } = application;
  return (
    <main className="min-h-screen bg-slate-100 px-4 py-7 sm:px-6 sm:py-10" data-testid="dealer-application-view">
      <div className="mx-auto max-w-4xl ab-fade-up">
        <header className="bg-slate-950 px-6 py-7 text-white sm:px-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-rose-300">
                AutoBroker · Confidential dealer packet
              </p>
              <h1 className="mt-2 font-display text-3xl font-black sm:text-4xl" data-testid="dealer-view-vehicle">
                {vehicle.label}
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                VIN {vehicle.vin || "Not provided"} · Stock {vehicle.stock_number || "Not provided"}
              </p>
            </div>
            <span
              className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-xs font-bold text-emerald-200"
              data-testid="dealer-view-secure-badge"
            >
              <ShieldCheck className="h-4 w-4" /> Verified secure session
            </span>
          </div>
        </header>
        <div className="bg-white px-6 py-7 shadow-xl sm:px-8 sm:py-9">
          <FieldGrid
            title="Applicant contact"
            testId="dealer-applicant-section"
            fields={[
              ["Full legal name", applicant.full_name],
              ["Email", applicant.email],
              ["Phone", applicant.phone],
              ["Registration address", applicant.address],
              ["Preferred contact", title(applicant.preferred_contact)],
            ]}
          />
          <FieldGrid
            title="Full financing application"
            testId="dealer-financing-section"
            fields={[
              ["Date of birth", financing.date_of_birth, true],
              ["Social Security number", financing.ssn, true],
              ["Annual income", money(financing.annual_income)],
              ["Employment status", title(financing.employment_status)],
              ["Employer", financing.employer_name],
              ["Employer address", financing.employer_address],
              ["Employment start date", financing.employment_start_date],
              ["Residence status", title(financing.residence_status)],
              ["Monthly housing", money(financing.monthly_housing)],
              ["Other monthly debts", money(financing.monthly_debts)],
              ["Down payment", money(financing.down_payment)],
              ["Requested term", financing.requested_term_months ? `${financing.requested_term_months} months` : ""],
              ["Self-reported credit tier", title(financing.credit_tier)],
              ["Submitted", financing.submitted_at],
            ]}
          />
          <FieldGrid
            title="Prequalification form"
            testId="dealer-prequal-section"
            fields={[
              ["Prequalification status", title(prequalification.status)],
              ["Budget", money(prequalification.budget_amount)],
              ["Budget type", title(prequalification.budget_type)],
              ["Prequal down payment", money(prequalification.down_payment)],
              ["Self-reported credit tier", title(prequalification.credit_tier)],
              ["Trade-in", prequalification.trade_in ? "Yes" : "No"],
              ["ZIP code", prequalification.zip_code],
              ["Purchase timeline", prequalification.timeline],
              ["Employment status", title(prequalification.employment_status)],
              ["Employer / business", prequalification.employer_name],
              ["Job title", prequalification.job_title],
              ["Time on job", prequalification.months_on_job ? `${prequalification.months_on_job} months` : ""],
              ["Monthly income", money(prequalification.monthly_income)],
              ["Submitted", prequalification.submitted_at],
            ]}
          />
          <section className="border-t border-slate-200 py-6" data-testid="dealer-receipt-section">
            <h2 className="font-display text-xl font-black text-slate-950">Receipt confirmation</h2>
            {receipt?.acknowledged_at ? (
              <p
                className="mt-3 border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800"
                data-testid="dealer-receipt-confirmed"
              >
                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                  <CheckCircle2 className="h-4 w-4" />
                  Receipt confirmed
                </span>
                <span className="mt-1 block whitespace-nowrap sm:ml-2 sm:mt-0 sm:inline">
                  {new Date(receipt.acknowledged_at).toLocaleString()}
                </span>
              </p>
            ) : (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-4 border border-slate-200 bg-slate-50 p-4">
                <p className="max-w-xl text-sm leading-6 text-slate-600">
                  Confirm receipt to let the AutoBroker team know this application reached the
                  authorized dealer contact.
                </p>
                <Button
                  type="button"
                  onClick={acknowledgeReceipt}
                  disabled={acknowledging}
                  className="shrink-0 rounded-none bg-slate-950 font-bold hover:bg-slate-800"
                  data-testid="dealer-acknowledge-receipt"
                >
                  {acknowledging ? "Confirming…" : "Confirm receipt"}
                </Button>
              </div>
            )}
          </section>
          <footer className="border-t border-slate-200 pt-5 text-xs leading-5 text-slate-500" data-testid="dealer-view-footer">
            Confidential applicant information supplied for this transaction. This time-limited
            view is logged and may be revoked by AutoBroker at any time.
          </footer>
        </div>
      </div>
    </main>
  );
}